import {
  GenericField,
  InputProps,
} from "@/lib/Components/Form/Fields/GenericField";
import { useState } from "react";
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  parsePhoneNumberWithError,
} from "libphonenumber-js/min";
import { useField } from "formik";
import { Region } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import { clsx } from "clsx";

const regionCountryCodeMap: {
  [key in Region]: CountryCode;
} = {
  [Region.Au]: "AU",
  [Region.Eu]: "GB",
  [Region.Nz]: "NZ",
  [Region.Us]: "US",
  [Region.Ca]: "CA",
  [Region.Pi]: "FJ",
  [Region.Jp]: "JP",
  [Region.Sacu]: "ZA",
  [Region.Tw]: "TW",
  [Region.Sa]: "BR",
  [Region.Me]: "AE",
  [Region.Mx]: "MX",
  [Region.Kr]: "KR",
};

type PhoneInputProps = InputProps & {
  defaultRegion: Region;
};

export function PhoneInput({ defaultRegion, ...props }: PhoneInputProps) {
  const [field, meta, { setValue }] = useField<string | null>(props.name);
  const { initialValue } = meta;

  const parsedNumber = parsePhoneNumberFromString(initialValue ?? "", {
    defaultCountry: regionCountryCodeMap[defaultRegion],
  });

  const [country, setCountry] = useState<CountryCode>(
    parsedNumber?.country || regionCountryCodeMap[defaultRegion],
  );

  const showError = meta.touched && !!meta.error;

  return (
    <GenericField {...props} viewNode={<span>{field.value || "--"}</span>}>
      {() => (
        <div
          data-slot="control"
          className={clsx(
            "relative block w-full",
            // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
            "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-sm",
            // Focus ringasdas
            "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
            // Disabled state
            "has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none",
            // Invalid state
            "has-data-invalid:before:shadow-red-500/10",
          )}
        >
          <div
            className={clsx(
              "relative block w-full rounded-lg",
              "data-hover:border-zinc-950/20 border border-zinc-950/10",
            )}
          >
            <div className="absolute inset-y-0 left-0 flex items-center">
              <label htmlFor="country" className="sr-only">
                Country
              </label>
              <select
                name="country"
                autoComplete="country"
                className="relative z-10 h-full rounded-md border-transparent bg-transparent py-0 pl-3 pr-7 text-base/6 text-gray-500 focus:border-indigo-500 focus:ring-indigo-500"
                value={country}
                onChange={(e) => setCountry(e.target.value as CountryCode)}
              >
                {getCountries().map((c) => (
                  <option
                    key={c}
                    value={c}
                    title={`(+${getCountryCallingCode(c)})`}
                  >
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <input
              {...field}
              value={field.value ?? ""}
              type="text"
              className={cn(
                "relative rounded-lg text-sm/6",
                "block w-full rounded-md border-gray-300 pl-24",
                "focus:outline-hidden py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                "data-hover:border-zinc-950/20 border border-zinc-950/10",
                showError
                  ? "border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500"
                  : "focus:border-indigo-500 focus:ring-indigo-500",
              )}
              onBlur={(e) => {
                try {
                  const phoneNumber = parsePhoneNumberWithError(
                    field.value ?? "",
                    country,
                  );

                  setValue(phoneNumber.formatInternational());
                  field.onBlur(e);

                  return;
                } catch (error) {
                  console.error(error);
                  field.onBlur(e);
                  return;
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  try {
                    const phoneNumber = parsePhoneNumberWithError(
                      field.value ?? "",
                      country,
                    );

                    setValue(phoneNumber.formatInternational());
                  } catch (e) {
                    console.error(e);
                  }
                }
              }}
            />
          </div>
        </div>
      )}
    </GenericField>
  );
}
